import './SearchPage.css';

import { observer } from 'mobx-react';
import React, { Fragment } from 'react';
import ReactPaginate from 'react-paginate';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

import { SearchEntryState } from './SearchEntryState';
import SearchPageState from './SearchPageState';
import { SearchTabState } from './SearchTabState';
import { WithTranslations } from '../Utils';
import css from './SearchPage.module.css';
import InputSearch from './InputSearch/InputSearch';
import classNames from 'classnames';
import ThreeDotsLoader from './ThreeDotsLoader/ThreeDotsLoader';
import Truncate from 'react-truncate';


type SearchPageProps = { state: SearchPageState } & WithTranslations;
@observer
export class SearchPage extends React.Component<SearchPageProps, { search: string, isActiveTabMobileOpen: boolean }> {

  constructor(props: SearchPageProps) {
    super(props);
    this.state = { isActiveTabMobileOpen: false, search: props.state.keyword };

    this.handleActiveTabMobileClick = this.handleActiveTabMobileClick.bind(this);
  }

  handleActiveTabMobileClick() {
    this.setState({ isActiveTabMobileOpen: !this.state.isActiveTabMobileOpen })
  }

  renderNavigationHeader() {
    const pages = this.props.state.tabs;
    const translations = this.props.translations;

    const container = (content: React.ReactElement | null) => {
      return <TabList className={css.tabList}>{content}</TabList>;
    }

    return container(this.props.state.isLoading ? null : (
      <div className={css.tabListContent}>
        <div className={classNames(css.tabNamesContainer, this.state.isActiveTabMobileOpen && css.isVisible)}>
          {pages.map(x => this.renderTabNames(x))}
        </div>

        {pages[this.props.state.tabIndex] && (<button className={css.selectedTabButton} onClick={this.handleActiveTabMobileClick}>
          <div>
            <span>{pages[this.props.state.tabIndex].displayName} ({pages[this.props.state.tabIndex].count})</span>
            <i className={classNames(css.angleIcon, this.state.isActiveTabMobileOpen ? "iconAngleUp" : "iconAngleDown")} />
          </div>
        </button>)}

        <span className={css.resultsCountText}>{translations.tab.allResults} {this.props.state.allResultsCount}</span>
      </div>
    ))
  }

  renderContent() {
    const pages = this.props.state.tabs;
    return (
      <div className={css.tabContent}>
        {pages.map(x => this.renderTabBody(x))}
      </div>
    );
  }
  
  renderTabNames(entry: SearchTabState) {
    const providers = this.props.translations.providers;
    
    const key = (Object.keys(providers) as Array<keyof typeof providers>).find(x => x.localeCompare(entry.displayName, undefined, {sensitivity: 'base'}) === 0);
    const value = key ? this.props.translations.providers[key] : entry.displayName;
    
    return (
      <Tab key={entry.displayName} className={css.tabHeader}>
        {value} <span className={css.count}>({entry.count})</span>
      </Tab>
    )
  }

  renderEntry(entry: SearchEntryState) {
    if (entry.type === 'Articles') {
      return (
        <a className={css.articleContainer} key={entry.url} href={entry.url}>
          <h3 className={css.articleTitle} dangerouslySetInnerHTML={({ __html: entry.title })} />
          {entry.description &&
            <Truncate lines={2} className={css.articleDescription}>
              <p dangerouslySetInnerHTML={({ __html: entry.description })} />
            </Truncate>
          }
          <a className={css.articleLink} href={entry.url} dangerouslySetInnerHTML={({ __html: entry.url })} />
        </a>
      );
    } else if (entry.type === 'Products') {
      return (
        <a className={css.productContainer} key={entry.url} href={entry.url}>
          <div className={css.productImageContainer}>
            {entry.image && <img className={css.productImage} alt={entry.title} src={entry.image} />}
          </div>
          <div className={css.productContent}>
            <h3 className={css.productTitle} dangerouslySetInnerHTML={({ __html: entry.title })} />
            {entry.description &&
              <Truncate lines={2} className={css.productDescription}>
                <p className={css.productDescription} dangerouslySetInnerHTML={({ __html: entry.description })} />
              </Truncate>
            }
            <a className={css.productLink} href={entry.url}>{this.props.translations.tab.learnMore}</a>
          </div>
        </a>
      );
    }
  }

  renderLoader() {
    return (
      <div className={css.loaderContainer}>
        <ThreeDotsLoader className={css.loader} size="large" />
      </div>
    )
  }

  renderContentLoader() {
    return (
      <div className={css.loaderContentContainer}>
        <ThreeDotsLoader className={css.loaderContent} size="medium" />
      </div>
    )
  }

  renderTabBody(entry: SearchTabState) {
    const tabTranslation = this.props.translations.tab;

    let body = this.renderLoader();
    if (!this.props.state.isLoading) {
      if (!this.props.state.keyword) {
        body = (<h2 className={css.infoText}>{tabTranslation.noQuery}</h2>);
      } else {
        if (!entry.isLoading && entry.entries.length === 0) {
          body = (
            <Fragment>
              <h2 className={classNames(css.infoText, css.quotation)}>{this.props.state.keyword}</h2>
              <h3 className={css.infoTextSmall}>{tabTranslation.notFound}</h3>
            </Fragment>
          );
        } else {
          body = (
            <div>
              <div className={css.searchResultContent}>
                {entry.isLoading && this.renderContentLoader()}
                {entry.entries.map(x => this.renderEntry(x))}
              </div>
              {this.renderPagination(entry)}
            </div>
          );
        }
      }
    }
    return (
      <TabPanel key={entry.displayName}>
        {body}
      </TabPanel>
    )
  }

  renderPagination(entry: SearchTabState) {

    return entry.allPages > 1 && (
      <ReactPaginate
        pageCount={entry.allPages}
        initialPage={entry.page}
        forcePage={entry.page}
        nextLabel={<i className="iconAngleRight" />}
        previousLabel={<i className="iconAngleLeft" />}
        marginPagesDisplayed={1}
        pageRangeDisplayed={1}
        onPageChange={(x) => {
          window.scroll({ top: 0, behavior: 'smooth' });
          this.props.state.setPage(x.selected)
        }}
        containerClassName={css.paginationContainer}
        breakClassName={css.paginationBreak}
        breakLinkClassName={css.paginationItemLink}
        disabledClassName={css.paginationButtonDisabled}
        pageLinkClassName={css.paginationItemLink}
        nextClassName={css.paginationButtonNext}
        activeLinkClassName={css.paginationActiveItem}
        previousLinkClassName={css.paginationNavLink}
        nextLinkClassName={css.paginationNavLink}
      />
    )
  }

  render() {
    const showNavigationHeader = !this.props.state.isLoading && 
        (this.props.state.keyword && !!this.props.state.allResultsCount);
   
    return (
      <div className={css.SearchPage}>
        <div className={css.inputSearchContainer}>
          <InputSearch
            autoFocus
            className={css.inputSearch}
            classNameContainer={css.inputSearchBox}
            value={this.state.search}
            placeholderTranslation={this.props.translations.tab.search}
            onChange={x => {
              const value = x.currentTarget.value;
              this.props.state.setKeyword(value);
              this.setState({search: value})
              }
            }
            onCloseButton={() => {this.setState({search: ''})}}
          />
        </div>
        <Tabs
          selectedIndex={this.props.state.tabIndex}
          onSelect={(e) => this.props.state.setTab(e)}
          className={css.tabs}
          selectedTabClassName={css.selectedTab}
        >
          {showNavigationHeader && this.renderNavigationHeader()}
          {this.renderContent()}
        </Tabs>
      </div>
    );
  }
}
